import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type CitationMode = 'citation' | 'normal';
type AstroPromptMode = 'astro_standard_mode' | 'astro_advanced_mode';

interface VerticalCitationToggleProps {
  variant?: 'citation' | 'astro';
  mode: CitationMode | AstroPromptMode;
  onModeChange: (mode: CitationMode | AstroPromptMode) => void;
  disabled?: boolean;
  className?: string;
}

const VerticalCitationToggle: React.FC<VerticalCitationToggleProps> = ({ variant = 'citation', mode, onModeChange, disabled = false, className = '' }) => {
  const { t } = useTranslation('chat');
  const isChecked = variant === 'astro' ? mode === 'astro_advanced_mode' : mode === 'citation';
  const [showHint, setShowHint] = useState<boolean>(false);
  const messageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleToggle = () => {
    if (disabled) return;
    if (variant === 'astro') {
      const newMode: AstroPromptMode = isChecked ? 'astro_standard_mode' : 'astro_advanced_mode';
      onModeChange(newMode);
      return;
    }
    const newMode: CitationMode = isChecked ? 'normal' : 'citation';
    onModeChange(newMode);
  };

  // Handle the temporary hint display when switching to citation mode
  useEffect(() => {
    if (variant !== 'citation') {
      setShowHint(false);
      return;
    }
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }

    if (isChecked) {
      setShowHint(true);
      messageTimeoutRef.current = setTimeout(() => {
        setShowHint(false);
      }, 2000);
    } else {
      setShowHint(false);
    }

    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, [isChecked, variant]);

  // Define SVG paths for the two states
  const normalIconPath = "M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"; // A simple dash
  const citationIconPath = "M10.755 4.33A.75.75 0 009.245 5.67l.5 1.5H8a3.5 3.5 0 00-3.5 3.5v2.5a.75.75 0 001.5 0v-2.5a2 2 0 012-2h2.255l-.5 1.5a.75.75 0 101.41.42l1.5-4.5a.75.75 0 00-.91-.91z"; // Citation icon

  return (
    <div className={`sl-vertical-toggle flex flex-col items-center ${className}`}>
      <label
        htmlFor="vertical-citation-toggle"
        className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <input 
          type="checkbox" 
          id="vertical-citation-toggle" 
          className="sr-only peer" 
          checked={isChecked}
          disabled={disabled}
          onChange={handleToggle}
        />
        
        {/* The vertical track of the toggle */}
        <div className={`sl-vertical-toggle-track w-4 h-8 bg-gray-700 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500/50 peer-checked:bg-cyan-600 transition-all duration-300 ease-in-out ${isChecked ? 'sl-vertical-toggle-track-active' : ''}`}></div>
        
        {/* The thumb of the toggle */}
        <div className={`sl-vertical-toggle-thumb absolute left-0.5 top-[18px] bg-white border-gray-300 border h-3 w-3 rounded-full transition-all duration-300 ease-in-out peer-checked:-translate-y-4 flex items-center justify-center shadow-sm ${isChecked ? 'sl-vertical-toggle-thumb-active' : ''}`}>
          {/* The icon inside the thumb */}
          <svg className={`w-2 h-2 transition-colors duration-300 ease-in-out ${isChecked ? 'text-cyan-600' : 'text-gray-500'}`} fill="currentColor" viewBox="0 0 20 20">
            <path 
              fillRule="evenodd" 
              d={isChecked ? citationIconPath : normalIconPath} 
              clipRule="evenodd"
            />
          </svg>
        </div>
      </label>
      
      <span className={`sl-vertical-toggle-label mt-1 text-xs text-center font-medium transition-colors duration-300 min-h-4 ${isChecked ? 'sl-vertical-toggle-label-active text-cyan-400' : 'text-gray-400'}`}>
        {variant === 'astro'
          ? (isChecked ? t('chat.citation_toggle.detailed') : t('chat.citation_toggle.guidance'))
          : (isChecked ? (showHint ? t('chat.citation_toggle.next_hint') : t('chat.citation_toggle.citations')) : t('chat.citation_toggle.normal'))}
      </span>
    </div>
  );
};

export default VerticalCitationToggle;
